import { useState, useRef, useEffect } from 'react';
import {
  Button,
  Icon,
  ListItem,
  QualState,
  Screen,
  ScreenHeader,
  SectionHead,
  Select,
  StateBox,
  TierBadge,
  Workspace,
  WorkspacePane,
} from '@garnet/ui';
import type { IconName, ButtonVariant } from '@garnet/ui';
import type {
  Diligence,
  ReqComment,
  ReqDoc,
  Requirement,
  Suggestion,
  VerticalData,
} from '../data/types.js';
import type { Route } from '../nav.js';

/* Garnet · Requirements — the diligence loop.
   Every in-progress diligence that's waiting on the counterparty: structured
   requirements (5-state lifecycle) with lightweight clarification comments, plus
   auto-suggested asks from flagged checks that the analyst confirms. */

type ReqStatus = Requirement['status'];

interface StatusSpec {
  label: string;
  dot: string;
  bg: string;
  ink: string;
}

/* requirement lifecycle — five states, on the assurance spine */
const REQ_STATUS: Record<ReqStatus, StatusSpec> = {
  requested: { label: 'Requested', dot: 'var(--neutral)', bg: 'var(--neutral-bg)', ink: 'var(--neutral-ink)' },
  awaiting: { label: 'Awaiting counterparty', dot: 'var(--attest)', bg: 'var(--attest-bg)', ink: 'var(--attest-ink)' },
  received: { label: 'Received · under review', dot: 'var(--ink-2)', bg: 'var(--rule-soft)', ink: 'var(--ink)' },
  verified: { label: 'Verified · resolved', dot: 'var(--verified)', bg: 'var(--verified-bg)', ink: 'var(--verified-ink)' },
  rejected: { label: 'Rejected · needs revision', dot: 'var(--flag)', bg: 'var(--flag-bg)', ink: 'var(--flag-ink)' },
};

function StatusChip({ status, sm }: { status: ReqStatus; sm?: boolean }) {
  const s = REQ_STATUS[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: s.bg,
        color: s.ink,
        borderRadius: 'var(--radius-sm)',
        padding: sm ? '2px 7px' : '3px 9px',
        fontFamily: 'var(--mono)',
        fontSize: sm ? 10 : 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flex: 'none' }} />
      {s.label}
    </span>
  );
}

function dueLabel(d: number): { t: string; c: string } {
  if (d < 0) return { t: 'overdue ' + -d + 'd', c: 'var(--flag-ink)' };
  if (d === 0) return { t: 'due today', c: 'var(--attest-ink)' };
  return { t: 'due in ' + d + 'd', c: 'var(--muted)' };
}

/* counts that need the analyst: received (review) + requested (to send) + suggestions (to confirm) */
function needsYou(d: Diligence): number {
  return (
    d.requirements.filter((r) => r.status === 'received' || r.status === 'requested').length +
    d.suggestions.length
  );
}

/* ── one clarification comment ─────────────────────────── */
function Comment({ c }: { c: ReqComment }) {
  if (c.role === 'system') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
        <span style={{ flex: 1, height: 1, background: 'var(--rule-soft)' }} />
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            color: 'var(--muted)',
            whiteSpace: 'nowrap',
          }}
        >
          {c.text} · {c.at}
        </span>
        <span style={{ flex: 1, height: 1, background: 'var(--rule-soft)' }} />
      </div>
    );
  }
  const mine = c.role === 'analyst';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: mine ? 'flex-end' : 'flex-start',
        margin: '8px 0',
      }}
    >
      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginBottom: 3 }}>
        {mine ? (c.who ?? 'You') + ' · analyst' : (c.who ?? 'Counterparty') + ' · supplier'} · {c.at}
      </div>
      <div
        style={{
          maxWidth: '82%',
          fontSize: 12.5,
          lineHeight: 1.5,
          padding: '8px 11px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid ' + (mine ? 'var(--verified)' : 'var(--rule)'),
          background: mine ? 'var(--verified-bg)' : 'var(--panel)',
          color: mine ? 'var(--verified-ink)' : 'var(--ink)',
        }}
      >
        {c.text}
      </div>
    </div>
  );
}

/* ── document chip ─────────────────────────── */
function DocChip({ doc, onInbox }: { doc: ReqDoc; onInbox: () => void }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        border: '1px solid var(--rule)',
        borderRadius: 'var(--radius-md)',
        padding: '7px 11px',
        background: 'var(--panel)',
      }}
    >
      <Icon name="file" size={15} style={{ color: 'var(--muted)' }} />
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{doc.name}</span>
      <button
        onClick={onInbox}
        style={{
          background: 'transparent',
          border: 0,
          color: 'var(--verified-ink)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          cursor: 'pointer',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        view in inbox
      </button>
    </div>
  );
}

type ReqActionFn = (rid: string, action: string) => void;
type CommentFn = (rid: string, text: string) => void;

/* ── requirement card (expandable) ─────────────────────────── */
function ReqCard({
  r,
  open,
  onToggle,
  onComment,
  onAction,
  go,
}: {
  r: Requirement;
  open: boolean;
  onToggle: () => void;
  onComment: CommentFn;
  onAction: ReqActionFn;
  go: (route: Route) => void;
}) {
  const [draft, setDraft] = useState('');
  const due = dueLabel(r.dueDays);
  const inputRef = useRef<HTMLInputElement>(null);

  function send() {
    const v = draft.trim();
    if (!v) return;
    onComment(r.id, v);
    setDraft('');
  }

  const actionsMap: Record<ReqStatus, Array<[string, ButtonVariant, IconName, () => void]>> = {
    requested: [['Send to counterparty', 'primary', 'send', () => onAction(r.id, 'send')]],
    awaiting: [
      ['Mark document received', 'secondary', 'download', () => onAction(r.id, 'receive')],
      ['Send reminder', 'ghost', 'clock', () => onAction(r.id, 'remind')],
    ],
    received: [
      ['Verify · resolve', 'primary', 'check', () => onAction(r.id, 'verify')],
      ['Reject · needs revision', 'ghost', 'x', () => onAction(r.id, 'reject')],
    ],
    rejected: [['Mark re-submission', 'secondary', 'download', () => onAction(r.id, 'receive')]],
    verified: [['Reopen', 'ghost', 'refresh', () => onAction(r.id, 'reopen')]],
  };
  const actions = actionsMap[r.status];

  return (
    <div
      className="panel"
      style={{
        marginBottom: 10,
        overflow: 'hidden',
        borderColor:
          r.status === 'received'
            ? 'var(--ink-2)'
            : r.status === 'rejected'
              ? 'var(--flag)'
              : 'var(--rule)',
      }}
    >
      {/* head */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 14,
          alignItems: 'center',
          padding: '13px 16px',
          cursor: 'pointer',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.title}</span>
            {r.severity === 'blocking' && (
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  color: 'var(--flag-ink)',
                  background: 'var(--flag-bg)',
                  borderRadius: 2,
                  padding: '1px 5px',
                }}
              >
                blocking
              </span>
            )}
          </div>
          <div
            style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}
          >
            {r.check} · {r.docType}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {r.status !== 'verified' && (
            <span
              style={{ fontFamily: 'var(--mono)', fontSize: 11, color: due.c, whiteSpace: 'nowrap' }}
            >
              {due.t}
            </span>
          )}
          <StatusChip status={r.status} sm />
          <Icon
            name="chevron"
            size={15}
            style={{
              color: 'var(--muted-2)',
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.12s ease',
            }}
          />
        </div>
      </div>

      {/* body */}
      {open && (
        <div style={{ borderTop: '1px solid var(--rule)', padding: '14px 16px', background: '#fcfcfb' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>
              {r.id} · raised {r.raised}
            </span>
            {r.doc && <DocChip doc={r.doc} onInbox={() => go('inbox')} />}
          </div>

          {/* comments */}
          <div style={{ margin: '12px 0 4px' }}>
            {r.comments.length === 0 ? (
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11.5,
                  color: 'var(--muted-2)',
                  textAlign: 'center',
                  padding: '8px 0',
                }}
              >
                not yet sent — no clarifications
              </div>
            ) : (
              r.comments.map((c, i) => <Comment key={i} c={c} />)
            )}
          </div>

          {/* composer */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Add a clarification comment…"
              style={{
                flex: 1,
                background: 'var(--panel)',
                border: '1px solid var(--rule)',
                borderRadius: 'var(--radius-md)',
                padding: '8px 11px',
                fontSize: 12.5,
                color: 'var(--ink)',
              }}
            />
            <Button sm variant="secondary" icon="send" onClick={send}>
              Comment
            </Button>
          </div>

          {/* actions */}
          {actions.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 13,
                paddingTop: 13,
                borderTop: '1px solid var(--rule-soft)',
                flexWrap: 'wrap',
              }}
            >
              {actions.map(([label, variant, icon, fn]) => (
                <Button key={label} sm variant={variant} icon={icon} onClick={fn}>
                  {label}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── suggestion card (auto from a finding) ─────────────────────────── */
function SuggestionCard({
  s,
  onRaise,
  onDismiss,
}: {
  s: Suggestion;
  onRaise: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        border: '1px dashed var(--rule)',
        borderRadius: 'var(--radius-md)',
        padding: '13px 15px',
        background: 'var(--rule-soft)',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Icon name="alert" size={14} style={{ color: 'var(--attest-ink)' }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</span>
        {s.severity === 'blocking' && (
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'var(--flag-ink)',
              background: 'var(--flag-bg)',
              borderRadius: 2,
              padding: '1px 5px',
            }}
          >
            blocking
          </span>
        )}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>
          {s.check}
        </span>
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: 'var(--muted)',
          lineHeight: 1.5,
          margin: '7px 0 11px',
          maxWidth: '70ch',
        }}
      >
        {s.rationale}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button sm variant="primary" icon="plus" onClick={onRaise}>
          Raise requirement
        </Button>
        <Button sm variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
        <span
          style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)', marginLeft: 2 }}
        >
          requests {s.docType.toLowerCase()}
        </span>
      </div>
    </div>
  );
}

/* ── manual raise form ─────────────────────────── */
interface FormState {
  title: string;
  check: string;
  docType: string;
  severity: 'blocking' | 'standard';
  dueDays: number;
}

function RaiseForm({ onAdd, onCancel }: { onAdd: (f: FormState) => void; onCancel: () => void }) {
  const [f, setF] = useState<FormState>({
    title: '',
    check: '',
    docType: '',
    severity: 'standard',
    dueDays: 7,
  });
  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setF((p) => ({ ...p, [k]: v }));
  }
  const ok = f.title.trim() && f.docType.trim();
  const field = {
    background: 'var(--panel)',
    border: '1px solid var(--rule)',
    borderRadius: 'var(--radius-md)',
    padding: '8px 11px',
    fontSize: 12.5,
    color: 'var(--ink)',
    width: '100%',
  } as const;
  const lab = {
    fontFamily: 'var(--mono)',
    fontSize: 10,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    display: 'block',
    marginBottom: 4,
  } as const;
  return (
    <div className="panel" style={{ padding: '16px 18px', marginBottom: 12, borderColor: 'var(--ink-2)' }}>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 12,
        }}
      >
        Raise a requirement
      </div>
      <div style={{ display: 'grid', gap: 11 }}>
        <div>
          <label style={lab}>What's needed</label>
          <input
            autoFocus
            style={field}
            value={f.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="e.g. Renewed type-approval certificate"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
          <div>
            <label style={lab}>Resolves check</label>
            <input
              style={field}
              value={f.check}
              onChange={(e) => set('check', e.target.value)}
              placeholder="c4 · certificate linkage"
            />
          </div>
          <div>
            <label style={lab}>Document type</label>
            <input
              style={field}
              value={f.docType}
              onChange={(e) => set('docType', e.target.value)}
              placeholder="Type-approval certificate"
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
          <div>
            <label style={lab}>Severity</label>
            <Select
              value={f.severity}
              onChange={(v) => set('severity', v as FormState['severity'])}
              ariaLabel="Severity"
              options={[
                { value: 'standard', label: 'Standard' },
                { value: 'blocking', label: 'Blocking' },
              ]}
            />
          </div>
          <div>
            <label style={lab}>Due in (days)</label>
            <input
              style={field}
              type="number"
              value={f.dueDays}
              onChange={(e) => set('dueDays', parseInt(e.target.value || '0', 10))}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Button sm variant="primary" icon="plus" disabled={!ok} onClick={() => onAdd(f)}>
          Add requirement
        </Button>
        <Button sm variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ── main ─────────────────────────── */
export function Requirements({ data, go }: { data: VerticalData; go: (r: Route) => void }) {
  const [diligences, setDiligences] = useState<Diligence[]>(() => structuredClone(data.diligences));
  const [selId, setSelId] = useState<string>(() => {
    const sorted = [...data.diligences].sort((a, b) => needsYou(b) - needsYou(a));
    return sorted[0]?.supplierId ?? '';
  });
  const [openReq, setOpenReq] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);

  const sel = diligences.find((d) => d.supplierId === selId);

  // open the first requirement that needs attention when switching diligences
  useEffect(() => {
    const cur = diligences.find((d) => d.supplierId === selId);
    if (!cur) return;
    const attn =
      cur.requirements.find((r) => r.status === 'received') ??
      cur.requirements.find((r) => r.status === 'requested') ??
      cur.requirements[0];
    setOpenReq(attn ? attn.id : null);
    setRaising(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId]);

  function mutate(fn: (d: Diligence) => Diligence) {
    setDiligences((prev) =>
      prev.map((d) => (d.supplierId === selId ? fn(structuredClone(d)) : d)),
    );
  }

  function addComment(rid: string, text: string) {
    mutate((d) => {
      const r = d.requirements.find((x) => x.id === rid);
      if (r) r.comments.push({ role: 'analyst', who: 'You', text, at: 'just now' });
      return d;
    });
  }

  function reqAction(rid: string, action: string) {
    mutate((d) => {
      const r = d.requirements.find((x) => x.id === rid);
      if (!r) return d;
      if (action === 'send') {
        r.status = 'awaiting';
        r.comments.push({ role: 'system', text: 'Sent to counterparty.', at: 'just now' });
      } else if (action === 'remind') {
        r.comments.push({ role: 'system', text: 'Reminder sent to counterparty.', at: 'just now' });
      } else if (action === 'receive') {
        r.status = 'received';
        if (!r.doc)
          r.doc = {
            name: r.docType.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.pdf',
            kind: 'document',
          };
        r.comments.push({
          role: 'supplier',
          who: d.supplier.split(' ').slice(0, 2).join(' '),
          text: 'Document submitted for review.',
          at: 'just now',
        });
        r.comments.push({ role: 'system', text: 'Document received · under review.', at: 'just now' });
      } else if (action === 'verify') {
        r.status = 'verified';
        r.comments.push({ role: 'system', text: 'Verified · resolved by you.', at: 'just now' });
      } else if (action === 'reject') {
        r.status = 'rejected';
        r.comments.push({
          role: 'system',
          text: 'Rejected · returned to counterparty for revision.',
          at: 'just now',
        });
      } else if (action === 'reopen') {
        r.status = 'awaiting';
        r.comments.push({ role: 'system', text: 'Reopened · awaiting counterparty.', at: 'just now' });
      }
      return d;
    });
    setOpenReq(rid);
  }

  function raiseSuggestion(sid: string) {
    mutate((d) => {
      const i = d.suggestions.findIndex((x) => x.id === sid);
      if (i < 0) return d;
      const s = d.suggestions[i];
      if (!s) return d;
      d.suggestions.splice(i, 1);
      const rid = s.id.replace('SUG', 'REQ');
      d.requirements.unshift({
        id: rid,
        title: s.title,
        check: s.check,
        docType: s.docType,
        severity: s.severity,
        dueDays: 7,
        status: 'requested',
        raised: 'just now',
        doc: null,
        comments: [],
      });
      return d;
    });
  }

  const dismissSuggestion = (sid: string) =>
    mutate((d) => {
      d.suggestions = d.suggestions.filter((x) => x.id !== sid);
      return d;
    });

  function addManual(f: FormState) {
    mutate((d) => {
      const n = d.requirements.filter((r) => r.id.startsWith('REQ-')).length + 1;
      d.requirements.unshift({
        id: d.recordId.replace('REC', 'REQ').replace(/-v\d+$/, '') + '-m' + n,
        title: f.title.trim(),
        check: f.check.trim() || 'manual',
        docType: f.docType.trim(),
        severity: f.severity,
        dueDays: f.dueDays,
        status: 'requested',
        raised: 'just now',
        doc: null,
        comments: [],
      });
      return d;
    });
    setRaising(false);
  }

  // ordered list: needs-you first, then most overdue
  const ordered = [...diligences].sort((a, b) => {
    const n = needsYou(b) - needsYou(a);
    if (n) return n;
    return (
      Math.min(...a.requirements.map((r) => r.dueDays)) -
      Math.min(...b.requirements.map((r) => r.dueDays))
    );
  });

  const statusCounts: Array<[ReqStatus, number]> = sel
    ? (Object.keys(REQ_STATUS) as ReqStatus[])
        .map((k): [ReqStatus, number] => [k, sel.requirements.filter((r) => r.status === k).length])
        .filter(([, n]) => n > 0)
    : [];

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Requirements"
        title="The diligence loop"
        lead="Diligences waiting on the counterparty. Verification surfaces a gap, you raise a requirement, and the back-and-forth is tracked until it resolves."
      />

      <Workspace rail={352}>
        {/* ── left rail · open diligences ── */}
        <WorkspacePane>
          <div
            className="pane-sticky"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 14px',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              Open diligences
            </span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              {diligences.length}
            </span>
          </div>
          <div className="list">
            {ordered.map((d) => {
              const ny = needsYou(d);
              const recv = d.requirements.filter((r) => r.status === 'received').length;
              return (
                <ListItem key={d.supplierId} active={d.supplierId === selId} onSelect={() => setSelId(d.supplierId)}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {d.supplier}
                    </span>
                    <TierBadge level={d.tier} sm showLabel={false} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <QualState state={d.state} />
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>
                      {d.requirements.length} open
                    </span>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    {ny > 0 ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontFamily: 'var(--mono)',
                          fontSize: 10.5,
                          color: 'var(--flag-ink)',
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: 'var(--flag)',
                            flex: 'none',
                          }}
                        />
                        {recv ? recv + ' to review' : ny + ' need you'}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontFamily: 'var(--mono)',
                          fontSize: 10.5,
                          color: 'var(--muted)',
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: 'var(--attest)',
                            flex: 'none',
                          }}
                        />
                        waiting {d.waiting}
                      </span>
                    )}
                  </div>
                </ListItem>
              );
            })}
          </div>
        </WorkspacePane>

        {/* ── right · selected diligence ── */}
        {sel ? (
          <WorkspacePane flush style={{ display: 'flex', flexDirection: 'column' }}><div>
            {/* header */}
            <div className="panel" style={{ padding: '16px 18px', marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 14,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                    {sel.flag} {sel.place} · {sel.recordId}
                  </div>
                  <h2
                    className="display"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      letterSpacing: '-.01em',
                      margin: '5px 0 0',
                      lineHeight: 1.1,
                    }}
                  >
                    {sel.supplier}
                  </h2>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 12,
                      color: 'var(--muted)',
                      marginTop: 6,
                    }}
                  >
                    {sel.asset}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TierBadge level={sel.tier} sm />
                    <QualState state={sel.state} />
                  </div>
                  <Button sm variant="secondary" iconRight="arrow" onClick={() => go('record')}>
                    Open full record
                  </Button>
                </div>
              </div>
              {/* status summary */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginTop: 14,
                  paddingTop: 13,
                  borderTop: '1px solid var(--rule-soft)',
                  alignItems: 'center',
                }}
              >
                {statusCounts.map(([k, n]) => (
                  <span
                    key={k}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--muted)',
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: REQ_STATUS[k].dot,
                        flex: 'none',
                      }}
                    />
                    <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{n}</b>{' '}
                    {REQ_STATUS[k].label.toLowerCase()}
                  </span>
                ))}
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                  waiting {sel.waiting}
                </span>
              </div>
            </div>

            {/* suggestions */}
            {sel.suggestions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <SectionHead
                  title="Suggested from findings"
                  sub="auto-raised from flagged checks · confirm to send"
                />
                {sel.suggestions.map((s) => (
                  <SuggestionCard
                    key={s.id}
                    s={s}
                    onRaise={() => raiseSuggestion(s.id)}
                    onDismiss={() => dismissSuggestion(s.id)}
                  />
                ))}
              </div>
            )}

            {/* requirements */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                }}
              >
                Requirements · {sel.requirements.length}
              </span>
              {!raising && (
                <Button sm variant="ghost" icon="plus" onClick={() => setRaising(true)}>
                  Raise a requirement
                </Button>
              )}
            </div>

            {raising && <RaiseForm onAdd={addManual} onCancel={() => setRaising(false)} />}

            {sel.requirements.map((r) => (
              <ReqCard
                key={r.id}
                r={r}
                go={go}
                open={openReq === r.id}
                onToggle={() => setOpenReq(openReq === r.id ? null : r.id)}
                onComment={addComment}
                onAction={reqAction}
              />
            ))}
          </div></WorkspacePane>
        ) : (
          <WorkspacePane flush>
            <StateBox kind="empty" title="Select a diligence" />
          </WorkspacePane>
        )}
      </Workspace>
    </Screen>
  );
}
